import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CaretDown, CaretRight, EnvelopeSimple, FilePdf, HardDrives } from "@phosphor-icons/react";
import { lies, schick } from "../../api/client";
import type { Projekt, Schreiben } from "../../api/types";
import { Meldung } from "../../components/Bausteine";
import { VersandDialog, VersandListe } from "../../components/Versand";
import { datum } from "../../lib/format";

const ART = { protokoll: "Protokoll", niederschrift: "Niederschrift", maengelanzeige: "Mängelanzeige" };

/** Was das Haus verlassen hat (E10) — mit Prüfsumme und Versandstand (SPEC §11.2). */
export function SchreibenReiter({ projekt }: { projekt: Projekt }) {
  const liste = useQuery({
    queryKey: ["schreiben", projekt.id],
    queryFn: () => lies<Schreiben[]>(`/projekte/${projekt.id}/schreiben`),
  });
  const [offen, setOffen] = useState<string | null>(null);
  const [senden, setSenden] = useState<Schreiben | null>(null);
  const leitung = projekt.meine_rolle === "leitung";
  return (
    <section className="karte">
      <div className="karte-kopf"><h2>Ausgangsschreiben</h2></div>
      <Meldung fehler={liste.error} />
      <div className="liste">
        {liste.data?.map((s) => (
          <div key={s.id}>
            <div className="zeile">
              <a href={`/api/schreiben/${s.id}/datei`} target="_blank" rel="noreferrer" aria-label="PDF öffnen"><FilePdf size={22} /></a>
              <button className="haupt knopf-text" onClick={() => setOffen(offen === s.id ? null : s.id)}>
                <div>{offen === s.id ? <CaretDown size={12} /> : <CaretRight size={12} />} {s.titel}</div>
                <div className="mute ziffern">
                  {ART[s.art]} · {datum(s.erzeugt_am)}{s.erzeugt_name && ` · ${s.erzeugt_name}`}
                  {s.verschickt ? ` · an ${s.verschickt} verschickt, ${s.abgerufen ?? 0} geöffnet` : " · noch nicht verschickt"}
                </div>
              </button>
              {leitung
                ? <button className="knopf" onClick={() => setSenden(s)}><EnvelopeSimple size={16} /> Versenden</button>
                : <span />}
            </div>
            {offen === s.id && (
              <div className="versand-aufklapp">
                <VersandListe schreibenId={s.id} />
                <ServerAblage s={s} projekt={projekt} leitung={leitung} />
                <div className="mute ziffern">SHA-256 {s.sha256}</div>
              </div>
            )}
          </div>
        ))}
        {liste.data?.length === 0 && <div className="leer">Noch nichts im Ausgang. Freigegebene Protokolle und abgeschlossene Niederschriften landen hier.</div>}
      </div>
      {senden && <VersandDialog schreibenId={senden.id} titel={senden.titel} zu={() => setSenden(null)} />}
    </section>
  );
}

/** Kopie im Projektordner auf dem Büro-Server (SPEC §11.4): wohin, oder warum nicht — und
 *  von Hand (nochmal) ablegen. */
function ServerAblage({ s, projekt, leitung }: { s: Schreiben; projekt: Projekt; leitung: boolean }) {
  const qc = useQueryClient();
  const jetzt = useMutation({
    mutationFn: () => schick(`/schreiben/${s.id}/serverablage`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schreiben", projekt.id] }),
  });
  const ordner = s.art === "protokoll" ? projekt.ablage_protokolle : (projekt.ablage_schreiben || projekt.ablage_protokolle);
  return (
    <div className="server-ablage">
      <HardDrives size={15} />
      {s.server_pfad
        ? <span>Kopie auf dem Server: <code>{s.server_pfad}</code></span>
        : s.server_fehler
          ? <span className="fehlertext">Kopie auf dem Server fehlgeschlagen: {s.server_fehler}</span>
          : <span className="mute">{ordner ? "Noch keine Kopie auf dem Server – sie entsteht beim Versand." : "Kein Ordner auf dem Büro-Server eingetragen (Übersicht → Einstellungen)."}</span>}
      {leitung && ordner && (
        <button className="knopf leise" disabled={jetzt.isPending} onClick={() => jetzt.mutate()}>
          {s.server_pfad ? "Nochmal ablegen" : "Jetzt ablegen"}</button>
      )}
      <Meldung fehler={jetzt.error} />
    </div>
  );
}
